package com.macrofocus.eflux.network

import com.macrofocus.eflux.diagram.DiagramType
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.ui.PaletteType
import korlibs.time.DateTime
import popper.core.Placement
import web.cssom.ClassName
import web.geometry.DOMPoint
import web.svg.SVGElement
import web.svg.SVGGraphicsElement
import kotlin.math.roundToInt
import kotlin.properties.Delegates

data class EFluxLink(override val branch: CombinedBranches?, val path: Path, val dateTime: DateTime, val diagramType: DiagramType) : Link {
    override val id: LogicalBranchId by path::id
    val USE_THICKNESS_CLASSES = false

    val elements: List<SVGElement> by path::elements
    override val longName: String = if(path.fullName.endsWith(")") && path.fullName.contains("(")) {
        path.fullName.substringBeforeLast("(")
    } else {
        path.fullName
    }

    init {
        val className = LinkField.baskv.getClassName(diagramType, this)
        path.voltage = className
    }

    var paletteType: PaletteType by Delegates.observable(PaletteType.dark) { _, _, _ ->
        updateStroke()
    }

    var inactive: Boolean by Delegates.observable(false) { _, _, _ ->
        updateStroke()
    }

    var probed: Boolean by Delegates.observable(false) { _, _, _ ->
        path.probed = probed
        updateStroke()
    }

    var selected: Boolean by Delegates.observable(false) { _, _, newValue ->
        path.selected = selected
        if(!newValue) {
            // Reset position
            clientOffsetX = 0.0
            clientOffsetY = 0.0
        }
        updateStroke()
    }

    var thickness: LinkField<*>? by Delegates.observable(null) { _, _, _ -> updateThickness() }
    var color: LinkField<*>? by Delegates.observable(null) { _, _, _ ->  updateStroke() }

    val landscape: Boolean
        get() {
            val boundingClientRect = (elements.first() as SVGGraphicsElement).getBBox()
            return boundingClientRect.width >= boundingClientRect.height
        }

    val anchorSVG : DOMPoint
        get() {
            val boundingClientRect = (elements.first() as SVGGraphicsElement).getBBox()
            return if(landscape) {
                val x = boundingClientRect.x + (boundingClientRect.width / 2.0)
                val y = boundingClientRect.y + boundingClientRect.height
                DOMPoint(x, y)
            } else {
                val x = boundingClientRect.x + boundingClientRect.width
                val y = boundingClientRect.y + (boundingClientRect.height / 2.0)
                DOMPoint(x, y)
            }
        }
    val anchorClient : DOMPoint
        get() {
            val boundingClientRect = elements.first().getBoundingClientRect()
            val x = boundingClientRect.x + (boundingClientRect.width / 2.0)
            val y = boundingClientRect.y + (boundingClientRect.height / 2.0)
            return DOMPoint(x, y)
        }
    var anchorEl = elements.first()
//    val anchorEl: Any
//        get() {
//            return object {
//                val clientWidth = elements.first().getBoundingClientRect().width
//                val clientHeight = elements.first().getBoundingClientRect().height
//                val getBoundingClientRect = {
//                    val boundingClientRect = elements.first().getBoundingClientRect()
//                    val x = boundingClientRect.x + (boundingClientRect.width / 2.0)
//                    val y = boundingClientRect.y + (boundingClientRect.height / 2.0)
//                    DOMRect(x, y, 0.0, 0.0)
//                }
//            }
//        }
    val placement = if(landscape) Placement.bottom else Placement.right

    /**
     * The top level SVG element. With SvgPanZoom, there are two nested SVG elements.
     */
    val ownerSVGElement = anchorEl.ownerSVGElement!!.ownerSVGElement!!

    /**
     * The SVG coordinates that can be used together with SvgPanZoom
     */
    val svgPanZoomCenter : DOMPoint
        get() {
            val svg = ownerSVGElement
            val svgBounds = svg.getBoundingClientRect()
            val bounds = boundingClientRect
            val point = svg.createSVGPoint()
            point.x = bounds.x - svgBounds.x
            point.y = bounds.y - svgBounds.y
            return point
        }

    val boundingClientRect : DOMPoint
        get() {
            val boundingClientRect = elements.first().getBoundingClientRect()
            val x = boundingClientRect.x + (boundingClientRect.width / 2.0)
            val y = boundingClientRect.y + (boundingClientRect.height / 2.0)
            return DOMPoint(x, y)
        }


    var clientOffsetX: Double = 0.0
        set(value) {
            field = value
            schemaX = screenToSVG(svgToScreen(anchorSVG).x + clientOffsetX, clientOffsetY).x
        }
    var clientOffsetY: Double = 0.0
        set(value) {
            field = value
            schemaY = screenToSVG(clientOffsetX, svgToScreen(anchorSVG).y + clientOffsetY).y
        }
    var schemaX: Double = screenToSVG(svgToScreen(anchorSVG).x + clientOffsetX, clientOffsetY).x
    var schemaY: Double =  screenToSVG(clientOffsetX, svgToScreen(anchorSVG).y + clientOffsetY).y

    val screenX: Double
        get() {
            return svgToScreen(schemaX, schemaY).x - svgToScreen(anchorSVG).x
        }
    val screenY: Double
        get() {
            return svgToScreen(schemaX, schemaY).y - svgToScreen(anchorSVG).y
        }

    fun screenToSVG(screenX : Double, screenY : Double) : DOMPoint {
        val p = DOMPoint(screenX, screenY)
        return screenToSVG(p)
    }

    fun screenToSVG(p : DOMPoint) : DOMPoint {
        return p.matrixTransform(ctm.inverse())
    }

    fun svgToScreen(svgX : Double, svgY : Double) : DOMPoint {
        val p = DOMPoint(svgX, svgY)
        return svgToScreen(p)
    }

    fun svgToScreen(p : DOMPoint) : DOMPoint {
        return p.matrixTransform(ctm)
    }

    val ctm
        get() = parentElement.getScreenCTM()!!

    val parentElement
        //        get() = elements.first().ownerSVGElement!!
        get() = elements.first().parentElement as SVGGraphicsElement

    fun updateStroke() {
        val className = color?.getClassName(diagramType, this)

        // If the class is defined, give it priority over the strokeColor
        if(className != null) {
            path.strokeColor = null
            path.voltage = className
        } else {
            val color = color?.getColor(this, paletteType)
            if (color != null) {
                path.strokeColor = color
            } else {
                // Set the default color
//                path.strokeColor = "#808080"
                path.strokeColor = null
            }
        }
    }

    fun updateThickness() {
        if(USE_THICKNESS_CLASSES) {
            val thickness = thickness
            if (thickness != null) {
                val normalized = thickness.getNormalized(this)
                val i = normalized?.times(10)?.roundToInt()
                if(i != null) {
                    path.thickness = ClassName("thickness$i")
                } else {
                    path.thickness = null
                }
            } else {
                path.thickness = null
            }
        } else {
            val thickness = thickness
            if (thickness != null) {
                val normalized = thickness.getNormalized(this)
                if (normalized != null) {
                    path.strokeWidth = (0.3 + (normalized * 2.7)).toString()
                } else {
                    path.strokeWidth = "0.0001"
                }
            } else {
                // Set the default thickness
                path.strokeWidth = null
            }
        }
    }
}
